import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Chart,
  LineElement,
  Legend,
  LinearScale,
  Tooltip,
  CategoryScale,
  PointElement,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import annotationPlugin from 'chartjs-plugin-annotation';
import intlShape from '@/shared/utils/intlShape';

import { listItemMsg } from '@/shared/components/FormattedListValue';
import { numberTooltipLabel } from '@/shared/utils/chartjsHelpers';
import authService from '@/shared/services/AuthService';
import { areHoursEqual } from './budgetHelpers';
import messages from './budgetMessages';

Chart.register(
  LineElement,
  Legend,
  LinearScale,
  Tooltip,
  CategoryScale,
  PointElement,
  annotationPlugin,
);

// Budget and planned duration are yearly totals with no monthly breakdown, so they are drawn as
// annotations: a dataset would invite the tooltip to report interpolated values that do not exist.
// The legend only lists datasets, so each annotation contributes a dataless one for its swatch.
// The plugin derives its hover tolerance from half the border width, so these sit above the
// default of 2 to give the reference lines a target that can actually be hit.
const referenceLineWidth = 3;
const legendSwatch = (label, borderColor) => ({
  label,
  data: [],
  borderColor,
  pointRadius: 0,
  type: 'line',
});

const BigBudgetGraph = ({ data, budget, plannedDuration, dataLabel, intl }) => {
  const [chartData, chartOptions] = useMemo(() => {
    if (!data) return [null, null];

    const hasBudget = budget > 0;
    const showPlannedDuration = plannedDuration > 0 && authService.hasFeature('CapacityPlanning');
    const linesCoincide =
      hasBudget && showPlannedDuration && areHoursEqual(budget, plannedDuration);
    // Which annotation the plugin reports on hover is arbitrary when two of them sit on the same
    // pixels, so when they coincide both carry one label that names the situation instead.
    const overlapLabel = intl.formatMessage(messages.BudgetEqualsPlannedDuration);

    const showLabelOnHover = {
      enter: ({ element }) => {
        element.label.options.display = true;
        return true;
      },
      leave: ({ element }) => {
        element.label.options.display = false;
        return true;
      },
    };

    const datasets = [
      {
        label: dataLabel,
        data,
        borderColor: '#0060ff',
        type: 'line',
      },
    ];
    if (hasBudget) datasets.push(legendSwatch(intl.formatMessage(messages.Budget), '#ffaa21'));
    if (showPlannedDuration)
      datasets.push(legendSwatch(intl.formatMessage(messages.PlannedDuration), '#8e44ad'));

    const cData = {
      labels: Array.from({ length: 12 }, (x, i) =>
        intl.formatMessage(
          listItemMsg(
            i + 1,
            'month',
            moment()
              .month(i)
              .format('MMMM'),
          ),
        ),
      ),
      datasets,
    };

    const budgetLevelAnnotation = {
      type: 'line',
      yMin: budget,
      yMax: budget,
      label: { content: intl.formatMessage(messages.Budget), display: false },
      borderColor: '#76a6af',
      borderWidth: referenceLineWidth,
      borderDash: [6, 6],
      borderDashOffset: 0,
      drawTime: 'beforeDatasetsDraw',
      ...showLabelOnHover,
    };
    const budgetAnnotation = {
      type: 'line',
      label: {
        content: linesCoincide ? overlapLabel : intl.formatMessage(messages.Budget),
        display: false,
      },
      borderColor: '#ffaa21',
      borderWidth: referenceLineWidth,
      // Interleaved with the planned-duration dashes below, so a pair sitting on the same pixels
      // reads as two lines whichever one is drawn on top.
      borderDash: linesCoincide ? [6, 6] : undefined,
      drawTime: 'beforeDatasetsDraw',
      yMin: 0,
      yMax: budget,
      ...showLabelOnHover,
    };
    const plannedDurationAnnotation = {
      type: 'line',
      label: {
        content: linesCoincide ? overlapLabel : intl.formatMessage(messages.PlannedDuration),
        display: false,
      },
      borderColor: '#8e44ad',
      borderWidth: referenceLineWidth,
      borderDash: linesCoincide ? [6, 6] : undefined,
      borderDashOffset: linesCoincide ? 6 : undefined,
      drawTime: 'beforeDatasetsDraw',
      yMin: 0,
      yMax: plannedDuration,
      ...showLabelOnHover,
    };

    const cOptions = {
      scales: {
        x: {
          grid: { display: true },
        },
        y: {
          ticks: {
            beginAtZero: true,
          },
        },
      },

      plugins: {
        annotation: {
          annotations: {
            ...(hasBudget ? { budgetLevelAnnotation, budgetAnnotation } : {}),
            ...(showPlannedDuration ? { plannedDurationAnnotation } : {}),
          },
        },
        tooltip: {
          callbacks: {
            label: numberTooltipLabel,
          },
        },
      },
    };

    return [cData, cOptions];
  }, [data, budget, plannedDuration, dataLabel, intl]);

  if (!data) return null;

  return (
    <div className="chart-container">
      <Line data={chartData} options={chartOptions} />
    </div>
  );
};

BigBudgetGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  budget: PropTypes.number,
  plannedDuration: PropTypes.number,
  dataLabel: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
};
BigBudgetGraph.defaultProps = {
  data: [],
  budget: 0,
  plannedDuration: 0,
};
export default BigBudgetGraph;
